import { Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { Link } from 'react-router-dom';
import { LabeledValue } from '@/components/ui/labeled-value';
import { StatusChip, type StatusChipVariant } from '@/components/ui/status-chip';
import type { BrokerType, FeeSplit, WireBroker } from '@/services/api/brokers';
import { BROKER_TYPE_LABELS } from './broker-options';

const BROKER_TYPE_CHIP_VARIANTS: Record<BrokerType, StatusChipVariant> = {
  TIED_AGENT: 'info',
  BROKER: 'warning',
};

/**
 * Port of legacy `feeSplitChipLabel` (pages/operations/brokers/index.vue:171),
 * except it returns null instead of an orphaned label.
 * Legacy bug fix: a stored `{ type: 'percentage' }` with neither `trades`
 * nor `liquidations` rendered a chip labelled just " fee split" — hide the chip
 * entirely when there are no parts.
 */
export const feeSplitChipLabel = (feeSplit: FeeSplit): string | null => {
  const parts: string[] = [];
  if (feeSplit.type === 'percentage') {
    if (feeSplit.trades !== undefined) parts.push(`T: ${feeSplit.trades}%`);
    if (feeSplit.liquidations !== undefined) parts.push(`L: ${feeSplit.liquidations}%`);
  } else {
    parts.push(`M: ${feeSplit.maker_fee_bps} bps`);
    parts.push(`T: ${feeSplit.taker_fee_bps} bps`);
    if (feeSplit.liquidations !== undefined) parts.push(`L: ${feeSplit.liquidations}%`);
  }
  if (parts.length === 0) return null;
  return `${parts.join(' / ')} fee split`;
};

interface BrokerCardProps {
  broker: WireBroker;
}

// Clickable card → broker detail. `broker.s` is the account holder UUID — the route
// param and identity — NOT `fast_id`.
export const BrokerCard = ({ broker }: BrokerCardProps) => {
  const feeSplitLabel = broker.fee_split ? feeSplitChipLabel(broker.fee_split) : null;

  return (
    <Link
      aria-label={`Open broker ${broker.name}`}
      className="block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      to={`/operations/brokers/${broker.s}`}
    >
      <Card className="h-full transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-md">
        <CardHeader>
          <CardTitle className="text-base font-semibold">{broker.name}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <LabeledValue label="Account ID" mono>
            {broker.s}
          </LabeledValue>
          <LabeledValue label="FAST ID" mono>
            {broker.fast_id}
          </LabeledValue>
          <div className="flex flex-wrap gap-1.5">
            <StatusChip className="px-2 py-0.5 text-xs" variant={BROKER_TYPE_CHIP_VARIANTS[broker.broker_type]}>
              {BROKER_TYPE_LABELS[broker.broker_type]}
            </StatusChip>
            {feeSplitLabel ? (
              <StatusChip className="whitespace-normal px-2 py-0.5 text-xs" variant="success">
                {feeSplitLabel}
              </StatusChip>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
BrokerCard.displayName = 'BrokerCard';
